import React from 'react';
import pt from 'prop-types';
import map from 'lodash/map';
import { connect } from 'react-redux';
import actions from 'partners/actions';
import { MainLayout } from 'partners/components/layout';
import { getImageFromFile, validateMaxFileSize } from 'utils';
import { MAX_UPLOAD_SIZE_TYPES } from 'constants/files';

const mapStateToProps = (state) => ({
  partner: state.partner,
  session: state.session,
  user: state.user,
  nav: state.nav,
  modal: state.modal,
  flash: state.flash,
});

const mapDispatchToProps = (dispatch) => ({
  uploadImage: (image) => {
    return dispatch(actions.files.uploadImage(image));
  },

  fetchAddress: (address) => {
    return dispatch(actions.lookup.fetchAddress(address));
  },

  queryLookupLoading: (value) => {
    return dispatch(actions.lookup.loading(value));
  },
  queryLookup: () => {
    throw new Error('queryLookup is not available in the partner portal');
  },
  clearLookup: () => {
    return dispatch(actions.lookup.clear());
  },

  toggleNavGroup: (id) => {
    dispatch(actions.nav.toggleGroup(id));
  },

  notifyCreated: (record) => {
    dispatch(
      actions.flash.success(
        `${record.name ? `${record.name} saved` : 'Saved'}`,
      ),
    );
  },

  notifySuccess: (message = 'Success!') => {
    dispatch(actions.flash.success(message));
  },

  notifyError: (message = 'An unknown error occurred') => {
    dispatch(actions.flash.error(message));
  },

  notifyDeleted: (type) => {
    dispatch(actions.flash.warning(`${type || 'Record'} deleted`));
  },

  notifyFileSizeUploadError: (record) => {
    dispatch(
      actions.flash.error(
        `File upload cannot exceed ${MAX_UPLOAD_SIZE_TYPES.default} MB`,
      ),
    );
  },

  notifySVGFileValidationUploadError: (fileName) => {
    dispatch(actions.flash.error(`Invalid SVG file`));
  },

  openModal: (id, props) => {
    dispatch(actions.modal.open(id, props));
  },

  closeModal: (id) => {
    dispatch(actions.modal.close(id));
  },

  refreshModal: () => {
    dispatch(actions.modal.refresh());
  },
});

export class Main extends React.Component {
  static propTypes = {
    router: pt.object,
    routes: pt.array,
    toggleNavGroup: pt.func,

    // mapStateToProps
    partner: pt.object,
    user: pt.object,
    uploadImage: pt.func,
    notifyFileSizeUploadError: pt.func,
    notifySVGFileValidationUploadError: pt.func,
    fetchAddress: pt.func,
    clearLookup: pt.func,
    queryLookup: pt.func,
    queryLookupLoading: pt.func,
    notifyCreated: pt.func,
    notifyError: pt.func,
    notifySuccess: pt.func,
    notifyDeleted: pt.func,

    // Modal context
    openModal: pt.func,
    closeModal: pt.func,
    refreshModal: pt.func,
  };

  static contextTypes = {
    router: pt.object.isRequired,
  };

  static onChange(store, params) {
    if (params.location.action !== 'REPLACE') {
      (document.getElementById('main') || window).scrollTop = 0;
    }
  }

  static childContextTypes = {
    uploadImages: pt.func,
    fetchAddress: pt.func,
    clearLookup: pt.func,
    queryLookup: pt.func,
    queryLookupLoading: pt.func,
    notifyCreated: pt.func,
    notifyError: pt.func,
    notifySuccess: pt.func,
    notifyDeleted: pt.func,
    openModal: pt.func,
    closeModal: pt.func,
    refreshModal: pt.func,
    getModalSavingActive: pt.func,
    setModalSavingActive: pt.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      showStoreMenu: false,
      modalSavingActive: false,
      onClickNavGroup: this.onClickNavGroup.bind(this),
    };
  }

  componentDidMount() {
    if (!this.requireUser(this.props)) {
      return;
    }

    this.setState({ loaded: true });
  }

  componentDidUpdate() {
    this.requireUser(this.props);
  }

  onClickNavUser(event) {
    event.preventDefault();
    if (this.mounted !== false) {
      this.setState({ showUserMenu: !this.state.showUserMenu });
    }
  }

  onClickNavGroup(event) {
    event.preventDefault();
    event.stopPropagation();
    this.props.toggleNavGroup(event.currentTarget.dataset.id);
  }

  requireUser(props) {
    const { session } = props;
    const { router } = this.context;
    const { pathname, search } = router.location;

    if (!session.user_id) {
      const path = pathname + search;

      if (path.length > 0) {
        router.replace(`/login?to=${encodeURIComponent(path)}`);
      } else {
        router.replace('/login');
      }

      return false;
    }

    return true;
  }

  getChildContext() {
    return {
      uploadImages: this.uploadImages.bind(this),
      fetchAddress: this.props.fetchAddress,
      clearLookup: this.props.clearLookup,
      queryLookup: this.props.queryLookup,
      queryLookupLoading: this.props.queryLookupLoading,
      notifyCreated: this.props.notifyCreated,
      notifyError: this.props.notifyError,
      notifySuccess: this.props.notifySuccess,
      notifyDeleted: this.props.notifyDeleted,
      openModal: this.props.openModal,
      closeModal: this.props.closeModal,
      refreshModal: this.props.refreshModal,
      getModalSavingActive: this.getModalSavingActive.bind(this),
      setModalSavingActive: this.setModalSavingActive.bind(this),
    };
  }

  uploadImages(files) {
    const { uploadImage, notifyError, notifyFileSizeUploadError } = this.props;

    if (
      !validateMaxFileSize(
        files,
        MAX_UPLOAD_SIZE_TYPES.default,
        notifyFileSizeUploadError,
      )
    ) {
      return;
    }

    return Promise.all(
      map(files, (file) => {
        return getImageFromFile(file).then((image) => {
          return uploadImage(image).then((result) => {
            if (result) {
              if (result.errors) {
                notifyError();
              } else {
                return result;
              }
            }
          });
        });
      }),
    );
  }

  getModalSavingActive() {
    return this.state.modalSavingActive;
  }

  setModalSavingActive(isActive) {
    this.setState({ modalSavingActive: isActive });
  }

  render() {
    if (!this.state.loaded) {
      return null;
    }

    // Render current route only
    if (this.props.routes !== this.props.router.routes) {
      return null;
    }

    return (
      <MainLayout
        {...this.props}
        {...this.state}
        onClickNavStore={this.onClickNavStore}
      />
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Main);
